/**
 * DeleteDataModal Component
 * Modal for confirming deletion of all user data
 * Requires typing "DELETE" to enable the delete button
 */
import React, { useState } from 'react';
import { deleteAllUserData } from '../../services/userDataService';
import { useAuth } from '../../context/AuthContext';

const DeleteDataModal = ({ isOpen, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [canClose, setCanClose] = useState(false);
  const { user } = useAuth();

  // Delay allowing close to prevent immediate closure from event propagation
  React.useEffect(() => {
    if (isOpen) {
      setCanClose(false);
      const timer = setTimeout(() => setCanClose(true), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const isDeleteEnabled = confirmText === 'DELETE';

  const handleDelete = async () => {
    if (!isDeleteEnabled || !user) return;

    setIsDeleting(true);
    setError('');

    try {
      const result = await deleteAllUserData(user.id);

      if (result.success) {
        // Redirect to homepage with success message
        // The signOut in deleteAllUserData will handle the redirect
        // But we also show a toast via URL parameter
        window.location.href = '/?deleted=true';
      } else {
        setError(result.error || 'Failed to delete data. Please try again.');
        setIsDeleting(false);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting && canClose) {
      setConfirmText('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  // Force visibility with inline styles to debug CSS issues
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 99999,
    padding: '1rem',
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    maxWidth: '480px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxHeight: '90vh',
    overflowY: 'auto',
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content delete-data-modal" style={modalStyle}>
        <button className="modal-close" onClick={handleClose} disabled={isDeleting}>
          &times;
        </button>

        <div className="delete-modal-header">
          <span className="warning-icon">&#9888;</span>
          <h2>Delete All Your Data?</h2>
        </div>

        <div className="delete-modal-body">
          <p className="delete-warning">This will permanently delete:</p>
          <ul className="delete-list">
            <li>All saved retirement plans</li>
            <li>All portfolio/investment data</li>
            <li>Your account and preferences</li>
          </ul>

          <p className="delete-danger">This action CANNOT be undone.</p>

          <div className="confirm-input-section">
            <label htmlFor="confirm-delete">Type DELETE to confirm:</label>
            <input
              id="confirm-delete"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="DELETE"
              disabled={isDeleting}
              autoComplete="off"
            />
          </div>

          {error && <p className="delete-error">{error}</p>}
        </div>

        <div className="delete-modal-actions">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isDeleting}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={!isDeleteEnabled || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Everything'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteDataModal;
